import { datePl, formatMoney, type InvoicePaymentResultDto, PAYMENT_METHODS, type PaymentMethod, type PendingInvoicePaymentDto, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { notifyError, notifyOk, notifyWarn } from '../notify';
import { PaymentAmountField, paymentEffect, usePaymentImpact } from './PaymentAmount';

/** Po wpłacie do faktury: stan faktur umowy, przegląd Finansów, należności i karta umowy. */
function useAfterPayment(contractId: string, onDone?: () => void) {
  const qc = useQueryClient();
  return (r: InvoicePaymentResultDto) => {
    qc.setQueryData(['contract-invoicing', contractId], r.invoicing);
    for (const key of [['invoicing-overview'], ['receivables'], ['contract', contractId], ['contract-payments', contractId], ['payment-impact', contractId]]) void qc.invalidateQueries({ queryKey: key });
    onDone?.();
  };
}

/**
 * „Zarejestruj wpłatę” przy fakturze końcowej / VAT: wpłata w CRM i w Fakturowni (Fakturownia zmienia status faktury).
 * Gdy Fakturownia nie odpowie — wpłata zostaje w CRM jako „do wysłania”, z przyciskiem ponowienia przy fakturze.
 */
export function InvoicePaymentModal({
  invoiceId,
  number,
  contractId,
  toPayGr,
  opened,
  onClose,
  onDone,
}: {
  invoiceId: string;
  number: string | null;
  contractId: string;
  toPayGr: number | null;
  opened: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  return (
    <Modal opened={opened} onClose={onClose} title={`Wpłata do faktury ${number ?? ''}`.trim()}>
      {opened && <InvoicePaymentForm invoiceId={invoiceId} contractId={contractId} toPayGr={toPayGr} onClose={onClose} onDone={onDone} />}
    </Modal>
  );
}

function InvoicePaymentForm({ invoiceId, contractId, toPayGr, onClose, onDone }: { invoiceId: string; contractId: string; toPayGr: number | null; onClose: () => void; onDone?: () => void }) {
  // QA qa10311123 3.1: bez podstawiania pełnej kwoty — wpisuje się kwotę z wyciągu albo „Cała kwota”
  const [amountGr, setAmountGr] = useState<number | null>(null);
  const impact = usePaymentImpact(contractId);
  const effect = paymentEffect(amountGr, impact.data);
  const [paidAt, setPaidAt] = useState(todayInWarsaw());
  const [method, setMethod] = useState<PaymentMethod>('przelew');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const after = useAfterPayment(contractId, onDone);
  const save = useMutation({
    mutationFn: () => api<InvoicePaymentResultDto>(`/invoices/${invoiceId}/payments`, { method: 'POST', body: { amountGr, paidAt, method, reference, note } }),
    onSuccess: (r) => {
      after(r);
      if (r.synced) notifyOk(`Zarejestrowano wpłatę ${formatMoney(amountGr ?? 0)} — stan faktury zaktualizowany w Fakturowni`);
      else notifyWarn(`Wpłata zapisana w CRM, ale Fakturownia jej nie przyjęła: ${r.error}. Ponów wysyłkę przy fakturze (CRM spróbuje też sam co 15 minut).`);
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Stack>
      {toPayGr !== null && (
        <Text size="sm">
          Pozostało do zapłaty: <b>{formatMoney(toPayGr)}</b>
        </Text>
      )}
      <PaymentAmountField amountGr={amountGr} onChangeGr={setAmountGr} remainingGr={toPayGr} what="faktura" impact={impact.data} />
      <TextInput label="Data wpłaty" type="date" max={todayInWarsaw()} value={paidAt} onChange={(e) => setPaidAt(e.currentTarget.value)} />
      <Select label="Sposób" data={Object.entries(PAYMENT_METHODS).map(([value, label]) => ({ value, label }))} value={method} onChange={(v) => v && setMethod(v as PaymentMethod)} allowDeselect={false} />
      <TextInput label="Tytuł przelewu lub nr dowodu wpłaty" placeholder="opcjonalnie" value={reference} onChange={(e) => setReference(e.currentTarget.value)} />
      <TextInput label="Opis" placeholder="opcjonalnie" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      <Text size="xs" c="dimmed">
        Wpłata trafi do CRM (rozłożona na płatności harmonogramu objęte fakturą, od najstarszej) i w całości do Fakturowni jako płatność tej faktury — Fakturownia oznaczy fakturę jako częściowo opłaconą albo opłaconą.
      </Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        <Button loading={save.isPending} disabled={!amountGr || amountGr <= 0 || (toPayGr !== null && amountGr > toPayGr)} color={effect === 'sprawa' ? 'orange' : undefined} onClick={() => save.mutate()}>
          {effect === 'sprawa' ? 'Zarejestruj i zamknij sprawę' : 'Zarejestruj wpłatę'}
        </Button>
      </Group>
    </Stack>
  );
}

/** Wpłaty z CRM, których Fakturownia jeszcze nie przyjęła — z ponowieniem. */
export function PendingInvoicePayments({ contractId, pending, canRetry, onDone }: { contractId: string; pending: PendingInvoicePaymentDto[]; canRetry: boolean; onDone?: () => void }) {
  const after = useAfterPayment(contractId, onDone);
  const retry = useMutation({
    mutationFn: (paymentId: string) => api<InvoicePaymentResultDto>(`/payments/${paymentId}/fakturownia-sync`, { method: 'POST' }),
    onSuccess: (r) => {
      after(r);
      if (r.synced) notifyOk('Wpłata przyjęta w Fakturowni');
      else notifyWarn(`Fakturownia nadal nie przyjęła wpłaty: ${r.error}`);
    },
    onError: notifyError,
  });
  if (!pending.length) return null;
  return (
    <Alert color="orange" variant="light" p={6} mt={4}>
      <Stack gap={4}>
        {pending.map((p) => (
          <Group key={p.paymentId} gap={6} wrap="nowrap" justify="space-between">
            <Text size="xs">
              Wpłata {formatMoney(p.amountGr)} z {datePl(p.paidAt)} — jeszcze nie w Fakturowni
            </Text>
            {canRetry && (
              <Button size="compact-xs" variant="light" color="orange" loading={retry.isPending && retry.variables === p.paymentId} onClick={() => retry.mutate(p.paymentId)}>
                Wyślij ponownie
              </Button>
            )}
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}
